<script setup lang="ts">
import { ref, computed, watch, useTemplateRef } from 'vue';
import { z } from 'zod';
import { UiFormBuilder, UiButton, UiText, UrgencyFormOptions } from '@shopexperts/shared';
import type { BuilderRowConfig, Urgency } from '@shopexperts/shared';
import type { LeadAdminResponse, AdminUpdateLeadRequest } from '../types';
import { useUpdateLead } from '../composables/useUpdateLead';
import { formatTimezoneWithOffset } from '../utils/timezone';

interface LeadFormProps {
    /** If provided, form is in edit mode */
    lead?: LeadAdminResponse | null;
}

const props = withDefaults(defineProps<LeadFormProps>(), {
    lead: null,
});

const emit = defineEmits<{
    (e: 'success'): void;
    (e: 'cancel'): void;
}>();

// Form mode
const isEditMode = computed(() => !!props.lead);

// Mutations
const { mutateAsync: updateLead, isPending: isUpdating } = useUpdateLead();

const isLoading = computed(() => isUpdating.value);
const budgetLabel = computed(() => props.lead?.budget_range || 'Flexible');
const timezoneLabel = computed(() => formatTimezoneWithOffset(props.lead?.timezone));

// Form schema
const schema = z.object({
    project_title: z.string().max(255, 'Project title must be 255 characters or less').optional(),
    project_brief: z.string().max(65535, 'Project brief is too long').optional(),
    urgency: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

// Initial values computed from prop
const initialValues = computed<FormValues>(() => ({
    project_title: props.lead?.project_title || '',
    project_brief: props.lead?.project_brief || '',
    urgency: props.lead?.urgency || '',
}));

// Form model
const formModel = ref<FormValues>({
    project_title: '',
    project_brief: '',
    urgency: '',
});

const formBuilderRef = useTemplateRef('formBuilderRef');

// Form rows configuration
const rows = computed<BuilderRowConfig[]>(() => [
    {
        columns: [
            { 
                type: 'text', 
                name: 'project_title', 
                label: 'Project Title', 
                props: { placeholder: 'E.g., Shopify store redesign' } 
            },
        ]
    },
    {
        columns: [
            { 
                type: 'textarea', 
                name: 'project_brief', 
                label: 'Project Brief', 
                props: { placeholder: 'Describe the project requirements...', rows: 4 } 
            },
        ]
    },
    {
        columns: [
            { 
                type: 'select', 
                name: 'urgency', 
                label: 'Project Timeline',
                props: {
                    options: [
                        { value: '', label: 'Select timeline...' },
                        ...UrgencyFormOptions,
                    ]
                }
            },
        ]
    },
]);

// Initialize form when editing
watch(() => props.lead, (lead) => {
    if (lead) {
        formModel.value = {
            project_title: lead.project_title || '',
            project_brief: lead.project_brief || '',
            urgency: lead.urgency || '',
        };
    } else {
        resetForm();
    }
}, { immediate: true });

function resetForm() {
    formModel.value = {
        project_title: '',
        project_brief: '',
        urgency: '',
    };
    formBuilderRef.value?.resetForm();
}

function buildPayload(values: FormValues): AdminUpdateLeadRequest {
    const payload: Record<string, any> = {};

    // Only include non-empty values
    if (values.project_title) payload.project_title = values.project_title;
    if (values.project_brief) payload.project_brief = values.project_brief;
    if (values.urgency) payload.urgency = values.urgency as Urgency;

    return payload as AdminUpdateLeadRequest;
}

async function handleSubmit(values: FormValues) {
    const payload = buildPayload(values);

    try {
        if (isEditMode.value && props.lead) {
            await updateLead({ id: props.lead.id, data: payload });
        }
        emit('success');
    } catch (error) {
        console.error('[LeadForm] Submit error:', error);
    }
}

function handleCancel() {
    resetForm();
    emit('cancel');
}

defineExpose({
    resetForm,
});
</script>

<template>
    <div
        v-if="lead"
        class="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:grid-cols-2"
    >
        <div>
            <UiText as="p" variant="caption-normal" class="text-gray-500">
                Budget
            </UiText>
            <UiText as="p" variant="p-small" class="font-semibold text-gray-900">
                {{ budgetLabel }}
            </UiText>
        </div>
        <div>
            <UiText as="p" variant="caption-normal" class="text-gray-500">
                Timezone
            </UiText>
            <UiText as="p" variant="p-small" class="font-semibold text-gray-900">
                {{ timezoneLabel }}
            </UiText>
        </div>
    </div>
    <UiFormBuilder 
        ref="formBuilderRef"
        v-model="formModel" 
        :schema="schema" 
        :rows="rows"
        :initial-values="initialValues"
        @submit="handleSubmit"
    >
        <template #actions>
            <!-- Action Buttons -->
            <div class="flex flex-col gap-3 pt-4 border-t border-gray-200">
                <div class="flex gap-3">
                    <UiButton
                        type="submit"
                        variant="primary"
                        label="Update Lead"
                        :loading="isLoading"
                        :disabled="isLoading"
                        class="flex-1"
                    />
                </div>

                <UiButton
                    type="button"
                    variant="ghost"
                    label="Cancel"
                    :disabled="isLoading"
                    @click="handleCancel"
                />
            </div>
        </template>
    </UiFormBuilder>
</template>
